import React, {
  FC,
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { SemItinerary, SemSearchResponse } from 'generated/graphql';
import { SearchQueryResult } from 'clientState/providers/SearchResponseProvider/types';
import { useItinerariesWithPriceResponseQuery } from 'clientState/providers/ItinerariesPricesProvider';
import { useTracking } from '@edreamsodigeo/frontend-core';

export const SORTING_LABELS = {
  cheapest: 'price',
  date: 'date',
};
const DEFAULT_SORTING = SORTING_LABELS.cheapest;
type keys = keyof typeof SORTING_LABELS;
type SortingType = (typeof SORTING_LABELS)[keys];

type SemSortingContextType = [
  SortingType,
  Dispatch<SetStateAction<SortingType>>
];

type ParsedSearchResponse = SemSearchResponse & {
  availableFilters: unknown;
  cheapest: {
    highlightedType: string;
    itinerary: SemItinerary;
    position: number;
  };
  cheapestItineraryPrice: number;
  currency: string;
  itineraries: SemItinerary[];
};

export const SemSortingRequestContext = createContext<SemSortingContextType>([
  DEFAULT_SORTING,
  (): SetStateAction<SortingType> => DEFAULT_SORTING,
]);

export const useSemSorting = (): SemSortingContextType =>
  useContext(SemSortingRequestContext);

const SemSortingProvider: FC = ({ children }) => {
  const [sorting, setSorting] = useState<SortingType>(DEFAULT_SORTING);

  const value = useMemo(() => [sorting, setSorting], [sorting, setSorting]);

  return (
    <SemSortingRequestContext.Provider value={value as SemSortingContextType}>
      {children}
    </SemSortingRequestContext.Provider>
  );
};

export const priceComparator = (a, b): number => {
  return a.price.amount - b.price.amount;
};
export const dateComparator = (a, b): number => {
  return (
    new Date(a.legs[0].segments[0].departureDate).valueOf() -
    new Date(b.legs[0].segments[0].departureDate).valueOf()
  );
};

const sortBy = {
  cheapest: priceComparator,
  date: dateComparator,
};

const useSemSortedItineraries = (
  sorting: string,
  search: ParsedSearchResponse
): unknown[] => {
  const [itineraryLists, setItineraryLists] = useState({});
  const cachedItineraries = itineraryLists[sorting];
  const itineraries = search?.itineraries;
  const result =
    itineraries &&
    (cachedItineraries || [...itineraries].sort(sortBy[sorting]));
  useEffect(() => {
    setItineraryLists({});
  }, [search, setItineraryLists]);
  useEffect(() => {
    if (result && !cachedItineraries) {
      setItineraryLists({
        ...itineraryLists,
        [sorting]: result,
      });
    }
  }, [setItineraryLists, itineraryLists, sorting, result, cachedItineraries]);

  return result;
};
type semSortingItinerariesQueryValue = {
  loading: boolean;
  error: SearchQueryResult['errors'] | null;
  data: ParsedSearchResponse | null;
  extensions: unknown;
};

const SemSortedItinerariesContext =
  createContext<semSortingItinerariesQueryValue>({
    loading: true,
    error: null,
    data: {} as ParsedSearchResponse,
    extensions: null,
  });

export const useSemSortedItinerariesQuery =
  (): semSortingItinerariesQueryValue =>
    useContext(SemSortedItinerariesContext);

export const SemSortedItinerariesProvider: FC = ({ children }) => {
  const [sorting] = useSemSorting();
  const { trackEvent } = useTracking();
  const searchQueryState = useItinerariesWithPriceResponseQuery();
  const { loading, error, data } = searchQueryState;
  const itineraries = useSemSortedItineraries(sorting, data);
  const [firstLoad, setFirstLoad] = useState(true);

  const value = useMemo(
    () =>
      loading || error || !itineraries
        ? {
            loading: loading || !itineraries,
            error,
            data: null,
            extensions: null,
          }
        : {
            loading: false,
            error: null,
            data: {
              ...data,
              itineraries,
            },
            extensions: null,
          },
    [loading, error, data, itineraries]
  );

  useEffect(() => {
    if (itineraries?.length > 0 && firstLoad) {
      trackEvent({
        action: 'results_filters',
        label: `sort_default_${sorting}`,
      });
      setFirstLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraries]);

  return (
    <SemSortedItinerariesContext.Provider value={value}>
      {children}
    </SemSortedItinerariesContext.Provider>
  );
};

export default SemSortingProvider;
